// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * A dialog component for marking assets as scrapped.
 * This module provides a form interface for recording when and why
 * an asset has been disposed of, dismantled, destroyed, or lost.
 * The component handles validation, date constraints, and optional
 * disposal price tracking for owned assets.
 *
 * Key features:
 * - Date validation
 * - Price tracking
 * - Reason logging
 * - Inventory warnings
 * - Form validation
 * - Error handling
 * - Ownership checks
 */
import { ScrapAssetParameters, scrapAsset } from '@/asset/api/scrap-asset';
import { PartialAsset } from '@/asset/api/types';
import { AssetInventoryDateWarning } from '@/asset/components/asset-inventory-date-warning';
import { isAssetOwned } from '@/asset/utilities/is-asset-owned';
import { FormCurrencyTextField } from '@/components/form-currency-text-field';
import { FormDatePicker } from '@/components/form-date-picker';
import { SupportSnackbar } from '@/components/support-snackbar';
import { MINIMUM_DATE } from '@/constants/date-shortcut';
import {
  SerializableCurrency,
  clearableCurrencyInputSchema,
} from '@/currency/api/types';
import { dateSchema } from '@/utilities/date-time-schema';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { SubmitHandler, useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';

/**
 * Schema for validating the scrap asset form data.
 * Ensures required fields are present and validates
 * date format and requirements.
 *
 * Validation rules:
 * - Date is required and must be a valid date on or after January 1, 1970
 * - Price is optional but must be a valid number if provided
 * - Reason is optional free-form text
 */
const formSchema = z
  .object({
    date: dateSchema.nullable(),
    price: clearableCurrencyInputSchema,
    reason: z.string().optional(),
  })
  .superRefine((data, context) => {
    if (data.date === null) {
      context.addIssue({
        path: ['date'],
        code: 'custom',
        message: 'Disposal date is required',
      });
      return;
    }

    if (data.date < MINIMUM_DATE) {
      context.addIssue({
        path: ['date'],
        code: 'custom',
        message: 'Disposal date cannot be before January 1, 1970',
      });
    }
  });

/**
 * Type definition for the form data structure.
 * Derived from the Zod validation schema.
 *
 * @typedef {Object} FormData
 *
 * @example
 * ```ts
 * const data: FormData = {
 *   date: DateTime.fromISO('2024-01-15'),
 *   price: { amount: new Decimal('1000.00'), isoCode: 'CAD' },
 *   reason: 'Equipment damaged beyond repair'
 * };
 * ```
 */
interface FormInput {
  date: DateTime | null;
  price: SerializableCurrency;
  reason?: string;
}

type FormData = z.output<typeof formSchema>;

/**
 * Represents the properties required to configure a component.
 */
interface Props {
  asset: PartialAsset;

  /**
   * Controls the visibility of the dialog.
   * When true, the dialog is displayed.
   *
   * @default false
   */
  isOpen: boolean;

  /**
   * Callback fired when the dialog should close.
   * Called after successful submission or when
   * the user cancels the operation.
   */
  onClose: () => void;
}

/**
 * AssetScrapDialog is a React component that displays a dialog for scrapping assets.
 * Users can provide necessary details such as disposal date, optional reason, and disposal price.
 * Once submitted, the input is validated and the asset is marked as scrapped.
 *
 * @param props - The properties passed to the dialog component
 * @param props.isOpen - A boolean indicating whether the dialog is open
 * @param props.onClose - A function to handle dialog closure
 * @param props.asset - The asset object containing details like `serial` and `key`
 *
 * @returns The AssetScrapDialog component
 */
export function AssetScrapDialog(props: Props) {
  const queryClient = useQueryClient();

  const { control, reset, register, handleSubmit } = useForm<
    FormInput,
    unknown,
    FormData
  >({
    resolver: zodResolver(formSchema),
    defaultValues: {
      date: null,
      price: { amount: '', isoCode: props.asset.accounting.costCurrencyCode },
      reason: '',
    },
  });

  const { mutate, isPending, isError, ...mutation } = useMutation({
    mutationFn: (parameters: ScrapAssetParameters) => {
      return scrapAsset(props.asset.key, parameters);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['asset'] });
      close();
    },
  });

  /**
   * Handles form submission after validation passes.
   * Transforms form data into API parameters and triggers
   * the scrap mutation if all required fields are present.
   *
   * @param data - Validated form data
   */
  const handleValidSubmission: SubmitHandler<FormData> = (data) => {
    if (data.date === null) {
      return;
    }

    const parameters: ScrapAssetParameters = {
      date: data.date,
    };

    if (data.price) {
      parameters.price = data.price;
    }

    if (data.reason) {
      parameters.reason = data.reason;
    }

    mutate(parameters);
  };

  /**
   * Handles dialog closure and form reset.
   * Clears form data and calls the onClose callback.
   */
  const close = () => {
    props.onClose();
    reset();
  };

  const dateInput = useWatch({ control, name: 'date' });

  return (
    <>
      <SupportSnackbar
        isOpen={isError}
        onClose={mutation.reset}
        message="An error occurred while scrapping the asset."
      />
      <Dialog
        open={props.isOpen}
        onClose={close}
        PaperProps={{
          component: 'form',
          onSubmit: handleSubmit(handleValidSubmission),
        }}
      >
        <DialogTitle>Scrap asset</DialogTitle>
        <DialogContent dividers>
          <Typography marginBottom={2}>
            Scrapping <b>{props.asset.serial}</b> will mark it as end of life
            when the asset has been disposed, dismantled, destroyed, or lost and
            is <b>not expected to return to service</b>.
          </Typography>
          <Alert severity="info">
            The asset will no longer appear unless it is searched explicitly,
            and will not be able to be assigned to projects. Historical
            reporting will be preserved.
          </Alert>
          <Stack direction="row" alignItems="center" marginTop={2}>
            <FormDatePicker
              disableFuture
              name="date"
              label="Disposal date"
              control={control}
              isDisabled={isPending}
            />
            <AssetInventoryDateWarning
              asset={props.asset}
              dateTime={dateInput}
            />
          </Stack>
          <TextField
            fullWidth
            size="small"
            label="Disposal reason (optional)"
            disabled={isPending}
            inputProps={register('reason')}
            sx={{ marginY: 2 }}
          />
          {isAssetOwned(props.asset) && (
            <FormCurrencyTextField
              fullWidth
              compact
              name="price"
              label="Disposal price (optional)"
              control={control}
              disabled={isPending}
              lockTo={props.asset.accounting.costCurrencyCode}
              disabledTooltip="A disposal is recorded in the asset's own currency"
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={close}>Cancel</Button>
          <Button variant="contained" type="submit" color="error">
            Scrap asset
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
